import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../../components/navbar';
import Card from '../../../components/card';
import { LoadingSkeleton } from '../../../components/common/LoadingComponents';
import { LeafletMapComponent, MapMarker } from '../../../components/maps/LeafletMapComponent';
import { HeatmapComponent } from '../../../components/maps/HeatmapComponent';
import { useAdminCache } from '../../../services/cache.service';
import customAxios from '../../../services/appservices';
import { useErrorHandler } from '../../../utils/error-handling.utils';
import { getRidesHeatmapApi } from '../../../services/customAPI';
import { HeatmapDataPoint, HeatmapDataType, MapFilters, OnlineDriver } from '../../../types/dashboard.types';
import { Types } from 'shared-lib';
import { Switch } from '@chakra-ui/react';
import {
  MdOutlineDirectionsCar,
  MdOutlineLocalFireDepartment,
  MdOutlineMap,
  MdOutlineRadar,
  MdOutlineHistory,
} from 'react-icons/md';
import car from '../../../assets/images/car.svg';

const DEFAULT_CENTER = { lat: -23.5443882, lng: -46.6494069 };

const MapsPage = () => {
  const { t } = useTranslation();
  const { getDrivers } = useAdminCache();
  const { handleApiError } = useErrorHandler();

  const [loading, setLoading] = useState(true);
  const [onlineDrivers, setOnlineDrivers] = useState<OnlineDriver[]>([]);
  const [historicPoints, setHistoricPoints] = useState<HeatmapDataPoint[]>([]);
  const [historicStats, setHistoricStats] = useState({ totalRides: 0, totalPoints: 0 });
  const [loadingHistoric, setLoadingHistoric] = useState(false);
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [filters, setFilters] = useState<MapFilters>({
    showDrivers: true,
    showHeatmap: true,
    heatmapType: HeatmapDataType.ALL,
  });
  const [showHistoric, setShowHistoric] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0],
  });

  // Load coverage area center
  useEffect(() => {
    const loadCoverageCenter = async () => {
      try {
        const res = await customAxios.get('/coverage-area');
        if (res?.data?.lat && res?.data?.lng) {
          setCenter({ lat: res.data.lat, lng: res.data.lng });
        }
      } catch {
        // Fallback to default center
      }
    };
    loadCoverageCenter();
  }, []);

  useEffect(() => {
    const loadDrivers = async () => {
      try {
        setLoading(true);
        const driversData = await getDrivers({ page: 1, limit: 100 });
        const onlineDriversWithLocation = (driversData?.data ?? [])
          .filter(
            (driver) =>
              driver.status === Types.DriverStatusEnum.ONLINE ||
              driver.status === Types.DriverStatusEnum.ON_RIDE
          )
          .map((driver: any) => ({
            id: driver.id,
            firstName: driver.firstName,
            lastName: driver.lastName,
            liveLocation: driver.liveLocation || [center.lng, center.lat],
            status: driver.status,
            vehicle: driver.vehicle || undefined,
          })) as OnlineDriver[];

        setOnlineDrivers(onlineDriversWithLocation);
      } catch (error) {
        handleApiError(error, 'Maps - loadDrivers');
      } finally {
        setLoading(false);
      }
    };
    loadDrivers();
  }, [getDrivers, handleApiError]);

  // Load historic heatmap data when toggled on
  useEffect(() => {
    if (!showHistoric) return;

    const loadHistoric = async () => {
      try {
        setLoadingHistoric(true);
        const res = await getRidesHeatmapApi({
          from: dateRange.from,
          to: dateRange.to,
          limit: 1000,
        });
        const payload = res?.data ?? res;
        const data = payload?.data;
        if (data?.points) {
          setHistoricPoints(
            data.points.map((p: any) => ({
              lat: p.lat,
              lng: p.lng,
              weight: p.type === 'pickup' ? 2 : 1,
              type: p.type === 'pickup' ? HeatmapDataType.RIDE_START : HeatmapDataType.RIDE_END,
              timestamp: new Date(p.date),
            }))
          );
          setHistoricStats({
            totalRides: data.totalRides,
            totalPoints: data.totalPoints,
          });
        }
      } catch (error) {
        handleApiError(error, 'Maps - loadHistoric');
      } finally {
        setLoadingHistoric(false);
      }
    };
    loadHistoric();
  }, [showHistoric, dateRange.from, dateRange.to, handleApiError]);

  const liveHeatmapData = useMemo((): HeatmapDataPoint[] => {
    const driverHeatPoints: HeatmapDataPoint[] = onlineDrivers.map((driver) => ({
      lat: driver.liveLocation[1],
      lng: driver.liveLocation[0],
      weight: driver.status === Types.DriverStatusEnum.ON_RIDE ? 3 : 1,
      type: HeatmapDataType.HOTSPOT,
      timestamp: new Date(),
    }));
    if (
      filters.heatmapType !== HeatmapDataType.ALL &&
      filters.heatmapType !== HeatmapDataType.HOTSPOT
    ) {
      return [];
    }
    return driverHeatPoints;
  }, [onlineDrivers, filters.heatmapType]);

  const combinedHeatmapData = useMemo(() => {
    const live = filters.showHeatmap ? liveHeatmapData : [];
    const historic = showHistoric ? historicPoints : [];
    return [...live, ...historic];
  }, [liveHeatmapData, historicPoints, filters.showHeatmap, showHistoric]);

  const driverMarkers: MapMarker[] = onlineDrivers.map((driver) => ({
    id: `driver-${driver.id}`,
    position: {
      lat: driver.liveLocation[1],
      lng: driver.liveLocation[0],
    },
    title: `${driver.firstName} ${driver.lastName}`,
    description: `Status: ${driver.status}${driver.vehicle ? `\nVeículo: ${driver.vehicle.vehicleNumber}` : ''}`,
    icon: car,
  }));

  const toHeatmapDataType = (value: string): HeatmapDataType => {
    const values = Object.values(HeatmapDataType);
    return values.includes(value as HeatmapDataType)
      ? (value as HeatmapDataType)
      : HeatmapDataType.ALL;
  };

  if (loading) {
    return (
      <div>
        <Navbar flag={false} brandText={t('Mapas')} />
        <div className="p-4">
          <LoadingSkeleton lines={4} />
        </div>
      </div>
    );
  }

  const onRideCount = onlineDrivers.filter(
    (d) => d.status === Types.DriverStatusEnum.ON_RIDE
  ).length;

  return (
    <div>
      <Navbar flag={false} brandText={t('Mapas')} />

      <div className="mt-4 space-y-4">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-green-500/15">
              <MdOutlineDirectionsCar className="h-6 w-6 text-green-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{onlineDrivers.length}</div>
              <div className="text-xs text-gray-400">{t('Motoristas Online')}</div>
            </div>
            {onRideCount > 0 && (
              <span className="ml-auto text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded-full">
                {onRideCount} em corrida
              </span>
            )}
          </div>

          <div className="flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-orange-500/15">
              <MdOutlineLocalFireDepartment className="h-6 w-6 text-orange-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{combinedHeatmapData.length}</div>
              <div className="text-xs text-gray-400">{t('Pontos de Calor')}</div>
            </div>
          </div>

          <div className="flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-purple-500/15">
              <MdOutlineHistory className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">
                {showHistoric ? historicStats.totalRides : '—'}
              </div>
              <div className="text-xs text-gray-400">{t('Corridas Históricas')}</div>
            </div>
          </div>

          <div className="flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-cyan-500/15">
              <MdOutlineRadar className="h-6 w-6 text-cyan-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{`${center.lat.toFixed(4)}, ${center.lng.toFixed(4)}`}</div>
              <div className="text-xs text-gray-400">{t('Área de Cobertura')}</div>
            </div>
          </div>
        </div>

        {/* Controls + Map */}
        <Card className="p-5">
          <div className="flex flex-wrap items-center gap-6 mb-4">
            <div className="flex items-center gap-3">
              <MdOutlineMap className="h-5 w-5 text-cyan-400" />
              <span className="text-lg font-semibold text-white">{t('Mapa Interativo')}</span>
            </div>

            <div className="flex items-center gap-6 ml-auto flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  isChecked={filters.showDrivers}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, showDrivers: e.target.checked }))
                  }
                  colorScheme="green"
                  size="md"
                />
                <span className="text-sm text-gray-300">{t('Motoristas')}</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  isChecked={filters.showHeatmap}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, showHeatmap: e.target.checked }))
                  }
                  colorScheme="orange"
                  size="md"
                />
                <span className="text-sm text-gray-300">{t('Calor (Tempo Real)')}</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  isChecked={showHistoric}
                  onChange={(e) => setShowHistoric(e.target.checked)}
                  colorScheme="purple"
                  size="md"
                />
                <span className="text-sm text-gray-300">{t('Histórico')}</span>
              </label>

              <select
                value={filters.heatmapType}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    heatmapType: toHeatmapDataType(e.target.value),
                  }))
                }
                className="rounded-xl bg-white/5 border border-white/10 text-sm text-gray-300 px-3 py-2 focus:outline-none focus:border-cyan-500/40"
              >
                <option value={HeatmapDataType.ALL}>{t('Todos os Dados')}</option>
                <option value={HeatmapDataType.RIDE_START}>{t('Início de Corridas')}</option>
                <option value={HeatmapDataType.RIDE_END}>{t('Fim de Corridas')}</option>
                <option value={HeatmapDataType.HOTSPOT}>{t('Pontos Quentes')}</option>
              </select>
            </div>
          </div>

          {/* Date range for historic data */}
          {showHistoric && (
            <div className="flex items-center gap-4 mb-4 p-3 rounded-xl bg-purple-500/5 border border-purple-500/20">
              <MdOutlineHistory className="h-5 w-5 text-purple-400" />
              <span className="text-sm text-gray-300">{t('Período')}:</span>
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange((d) => ({ ...d, from: e.target.value }))}
                className="rounded-lg bg-white/5 border border-white/10 text-sm text-gray-300 px-3 py-1.5 focus:outline-none focus:border-purple-400/50"
              />
              <span className="text-gray-500">→</span>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange((d) => ({ ...d, to: e.target.value }))}
                className="rounded-lg bg-white/5 border border-white/10 text-sm text-gray-300 px-3 py-1.5 focus:outline-none focus:border-purple-400/50"
              />
              {loadingHistoric && (
                <span className="text-xs text-purple-400 animate-pulse">{t('Carregando...')}</span>
              )}
              {!loadingHistoric && historicStats.totalPoints > 0 && (
                <span className="text-xs text-gray-400">
                  {historicStats.totalPoints} pontos de {historicStats.totalRides} corridas
                </span>
              )}
            </div>
          )}

          {/* Main Map */}
          <div className="rounded-2xl overflow-hidden border border-white/10" style={{ height: '600px' }}>
            <LeafletMapComponent
              center={center}
              zoom={11}
              markers={filters.showDrivers ? driverMarkers : []}
              heatmapData={combinedHeatmapData}
              showHeatmap={filters.showHeatmap || showHistoric}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        </Card>

        {/* Separate Heatmap Analysis */}
        {(filters.showHeatmap || showHistoric) && (
          <HeatmapComponent
            data={combinedHeatmapData}
            center={center}
            zoom={11}
            showControls={true}
            showLegend={true}
            className="w-full"
            filters={{
              dataType:
                filters.heatmapType === HeatmapDataType.ALL
                  ? undefined
                  : filters.heatmapType,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default MapsPage;
